/**
 * #323: pre-match loadout selection helpers — the client-side half of
 * "choose 12 abilities from a per-champion ability pool."
 *
 * See `packages/protocol/src/messages.ts`'s `LOADOUT_SCHEMA` doc comment for
 * the wire contract + the slot-order decision (a loadout is a SUBSET
 * selection, content-canonical slot order preserved, never a reorder).
 */

import { MAX_LOADOUT_SIZE, type AbilityId, type ChampionId } from "@pit/protocol";
import { abilityPoolFor } from "./abilityPool.js";

export { MAX_LOADOUT_SIZE };

/**
 * The default loadout for `championId`: every champion shipped today has a
 * pool of `MAX_LOADOUT_SIZE` or fewer abilities, so "no customization" means
 * "bring everything" — the whole pool, in content-canonical order. For a
 * (currently hypothetical) champion whose pool exceeds `MAX_LOADOUT_SIZE`,
 * this returns the first `MAX_LOADOUT_SIZE` as a starting selection the
 * player can then adjust in the interactive picker.
 */
export function defaultLoadout(championId: ChampionId): readonly AbilityId[] {
  const pool = abilityPoolFor(championId);
  return pool.length <= MAX_LOADOUT_SIZE ? pool : pool.slice(0, MAX_LOADOUT_SIZE);
}

/** Does `championId`'s pool exceed `MAX_LOADOUT_SIZE`? Only then is there a
 *  real choice to make — see `LoadoutPicker`'s skip-the-UI behavior. */
export function needsLoadoutChoice(championId: ChampionId): boolean {
  return abilityPoolFor(championId).length > MAX_LOADOUT_SIZE;
}

/**
 * #513: the champion's full pool, canonical-slot order, filtered down to
 * just the abilities present in `loadout` — the "logical slot list" the
 * in-match `ActionBar` maps its `ACTION_SLOT_COUNT` visual bar positions
 * onto (via #417's `SlotOrder`) instead of the raw, unfiltered pool.
 *
 * Each returned entry is a canonical slot index (a POSITION in the pool
 * array — the same index `abilityLabel`/`abilityFacts`/the wire snapshot's
 * `cast.slot`/`cooldowns[].slot` all key off), in ascending (pool) order —
 * mirrors `resolveLoadout`'s own canonical-order invariant on the server, so
 * slot identity here always matches what the picker/server agreed on.
 *
 * `loadout === null` (untracked — e.g. a pre-match demo/dev route that never
 * goes through the normal join flow) keeps the WHOLE pool: additive over the
 * pre-#513 "always render the raw canonical pool" behavior, never a
 * regression. A champion whose resolved loadout selects fewer than
 * `pool.length` abilities simply returns fewer entries — the caller fills
 * the first N bar positions and leaves the remainder empty (see
 * `ActionBar`), never assuming any particular count.
 */
export function loadoutCanonicalSlots(
  championId: ChampionId,
  loadout: readonly AbilityId[] | null,
): readonly number[] {
  const pool = abilityPoolFor(championId);
  if (loadout === null) return pool.map((_id, slot) => slot);
  const selected = new Set(loadout);
  const out: number[] = [];
  pool.forEach((id, slot) => {
    if (selected.has(id)) out.push(slot);
  });
  return out;
}
